import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TableStatesComponent } from '@components/table-states/table-states.component';
import { StatusPillComponent, PillTone } from '@components/status-pill/status-pill.component';
import { TimeAgoPipe } from '@common/pipes/time-ago.pipe';
import { formatSpeedAlarmReading } from '@common/index';
import {
  GetNotificationsParams,
  Notification,
  NotificationSeverity,
  NotificationService,
} from '@services/notification.service';
import { Pagination } from '@interfaces/index';

const POLL_INTERVAL_MS = 30_000;
const PER_PAGE = 20;

const SEVERITY_TONE: Record<NotificationSeverity, PillTone> = {
  info: 'info',
  warning: 'warn',
  critical: 'crit',
};

interface NotificationView {
  title: string;
  detail: string;
}

/**
 * One renderer per known `type`. Anything not listed here falls back to a
 * generic row (type + severity + timestamp only) instead of breaking —
 * more types get a real renderer added here as their producers ship.
 */
const TYPE_RENDERERS: Partial<Record<string, (n: Notification) => NotificationView>> = {
  kyc_review_pending: (n) => ({
    title: 'KYC review pending',
    detail: `${n.payload.driverName ?? 'Unknown driver'} — ${n.payload.driverEmail ?? ''}`,
  }),
  speed_limit_exceeded: (n) => ({
    title: 'Speed limit exceeded',
    detail: `${n.payload.plate ?? 'Unknown vehicle'}${n.payload.driverName ? ' · ' + n.payload.driverName : ''} — ${formatSpeedAlarmReading(n.payload)} in ${n.payload.zoneName ?? 'a speed zone'}`,
  }),
  out_of_zone: (n) => ({
    title: 'Out of zone',
    detail: `${n.payload.plate ?? 'Unknown vehicle'}${n.payload.driverName ? ' · ' + n.payload.driverName : ''} — left ${n.payload.zoneName ?? 'its pickup zone'}`,
  }),
};

interface RouteTarget {
  commands: string[];
  queryParams: Record<string, string>;
}

/**
 * One route per known `entityType` a click can deep-link to. Filters by
 * whatever's already visible on the destination screen (here, the KYC
 * queue's own free-text search) rather than an invisible id param, so the
 * active filter is self-evident once you land. No entry → click only marks
 * read, same as an unrecognised notification type.
 */
const ENTITY_ROUTES: Partial<Record<string, (n: Notification) => RouteTarget>> = {
  kyc_status: (n) => ({
    commands: ['/users'],
    queryParams: { search: n.payload.driverEmail ?? '' },
  }),
  // Fleet alarms (speed_alarm today, zone_alarm / late_return_alarm later)
  // all land on the command dashboard's Active Alarms panel, which selects
  // and scrolls to the matching alarm by its notification id.
  speed_alarm: (n) => ({
    commands: ['/dashboard'],
    queryParams: { alarmId: n.id },
  }),
  zone_alarm: (n) => ({
    commands: ['/dashboard'],
    queryParams: { alarmId: n.id },
  }),
};

@Component({
  selector: 'app-notification-panel',
  imports: [
    NzSpinModule,
    NzButtonModule,
    NzIconModule,
    TableStatesComponent,
    StatusPillComponent,
    TimeAgoPipe,
  ],
  templateUrl: './notification-panel.component.html',
  styleUrl: './notification-panel.component.scss',
})
export class NotificationPanelComponent {
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  /** Whether the dropdown is currently open — gates fetching and polling. */
  isOpen = input.required<boolean>();
  /** Emitted after a successful mark-read/mark-all-read, so the bell badge can refresh immediately. */
  readonly unreadChanged = output<void>();
  /** Emitted after navigating away from a row click, so the dropdown closes behind it. */
  readonly closed = output<void>();

  private page = signal(1);
  private pollTick = signal(0);
  private accumulated = signal<Notification[]>([]);
  private total = signal(0);
  /** Set once the user scrolls past page 1 — pauses the poll so it can't
   *  silently reset the list back to page 1 out from under a scrolled-down
   *  view. Resumes automatically the next time the panel is closed/reopened. */
  private hasLoadedMore = signal(false);
  private markingIds = signal<ReadonlySet<string>>(new Set());

  constructor() {
    // Re-fetch every POLL_INTERVAL_MS while open and still at page 1 — the
    // badge count already polls app-wide in LayoutComponent regardless.
    effect((onCleanup) => {
      if (!this.isOpen() || this.hasLoadedMore()) {
        return;
      }
      const handle = setInterval(() => this.pollTick.update((v) => v + 1), POLL_INTERVAL_MS);
      onCleanup(() => clearInterval(handle));
    });

    effect(() => {
      if (this.isOpen()) {
        this.page.set(1);
        this.accumulated.set([]);
        this.total.set(0);
        this.hasLoadedMore.set(false);
      }
    });

    // page 1 (initial load or a poll tick) replaces the list; any later page
    // (scrolled-to "load more") appends instead, deduped by id.
    effect(() => {
      const value = this.notificationsResource.value();
      if (!value) {
        return;
      }
      this.total.set(value.total);
      if (value.page === 1) {
        this.accumulated.set(value.results);
      } else {
        this.accumulated.update((prev) => {
          const seen = new Set(prev.map((n) => n.id));
          return [...prev, ...value.results.filter((n) => !seen.has(n.id))];
        });
      }
    });
  }

  private queryParams = computed<GetNotificationsParams | null>(() => {
    this.pollTick();
    if (!this.isOpen()) {
      return null;
    }
    return { page: this.page(), perPage: PER_PAGE };
  });

  notificationsResource = rxResource<Pagination<Notification>, GetNotificationsParams | null>({
    params: () => this.queryParams(),
    stream: ({ params }) => {
      if (!params) {
        return of({ page: 1, perPage: PER_PAGE, total: 0, results: [] as Notification[] });
      }
      return this.notificationService.getAll(params);
    },
  });

  isLoading = computed(() => this.notificationsResource.isLoading());
  /** Only true for the very first fetch — the big spinner shouldn't cover
   *  the whole list again for a background poll or a "load more" append. */
  isInitialLoading = computed(() => this.isLoading() && this.page() === 1);
  isLoadingMore = computed(() => this.isLoading() && this.page() > 1);
  notifications = computed(() => this.accumulated());

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) {
      this.loadMore();
    }
  }

  private loadMore(): void {
    if (this.isLoading() || this.accumulated().length >= this.total()) {
      return;
    }
    this.hasLoadedMore.set(true);
    this.page.update((p) => p + 1);
  }

  view(notification: Notification): NotificationView {
    const renderer = TYPE_RENDERERS[notification.type];
    return (
      renderer?.(notification) ?? {
        title: notification.type,
        detail: 'No details available for this notification type yet',
      }
    );
  }

  tone(notification: Notification): PillTone {
    return SEVERITY_TONE[notification.severity] ?? 'neutral';
  }

  isResolved(notification: Notification): boolean {
    return notification.actionRequired && notification.resolvedAt !== null;
  }

  isMarking(id: string): boolean {
    return this.markingIds().has(id);
  }

  isNavigable(notification: Notification): boolean {
    return !!notification.entityType && !!ENTITY_ROUTES[notification.entityType];
  }

  onRowClick(notification: Notification): void {
    if (!notification.isRead && !this.isMarking(notification.id)) {
      this.setMarking(notification.id, true);
      this.notificationService.markRead(notification.id).subscribe((ok) => {
        this.setMarking(notification.id, false);
        if (ok) {
          this.applyRead([notification.id]);
          this.unreadChanged.emit();
        }
      });
    }

    const route = notification.entityType ? ENTITY_ROUTES[notification.entityType] : undefined;
    if (route) {
      const { commands, queryParams } = route(notification);
      void this.router.navigate(commands, { queryParams });
      this.closed.emit();
    }
  }

  markAllRead(): void {
    const unreadIds = this.notifications()
      .filter((n) => !n.isRead)
      .map((n) => n.id);
    if (!unreadIds.length) {
      return;
    }
    this.notificationService.markAllRead().subscribe((ok) => {
      if (ok) {
        this.applyRead(unreadIds);
        this.unreadChanged.emit();
      }
    });
  }

  private setMarking(id: string, marking: boolean): void {
    const next = new Set(this.markingIds());
    if (marking) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.markingIds.set(next);
  }

  private applyRead(ids: string[]): void {
    const idSet = new Set(ids);
    this.accumulated.update((results) =>
      results.map((n) => (idSet.has(n.id) ? { ...n, isRead: true } : n)),
    );
  }
}
